import { Injectable } from '@angular/core';
import { BehaviorSubject, catchError, of } from 'rxjs';
import { ApiService } from './ApiService';
import { IdentityService } from './auth/identity.service';
import { IUserSetting } from '../interfaces/IUserSetting';

const SETTLE_MS = 400;

@Injectable({
  providedIn: 'root'
})
export class UserSettingsService {
  private readonly cache = new Map<string, unknown>();
  private readonly pending = new Map<string, ReturnType<typeof setTimeout>>();
  private hydratedForSession = false;
  private readonly loadedSubject = new BehaviorSubject<boolean>(false);
  readonly loaded$ = this.loadedSubject.asObservable();

  constructor(
    private readonly apiService: ApiService,
    identityService: IdentityService,
  ) {
    identityService.isAuthenticated.subscribe(ok => {
      if (!ok) {
        this.clear();
        return;
      }
      if (!this.hydratedForSession) {
        this.hydratedForSession = true;
        this.hydrate();
      }
    });
  }

  get<T>(key: string): T | undefined {
    if (!this.cache.has(key)) {
      return undefined;
    }
    return this.cache.get(key) as T;
  }

  set(key: string, value: unknown): void {
    this.cache.set(key, value);
    const prev = this.pending.get(key);
    if (prev) {
      clearTimeout(prev);
    }
    this.pending.set(key, setTimeout(() => {
      this.pending.delete(key);
      this.apiService.put(`/user-settings/${encodeURIComponent(key)}`, { value }).pipe(
        catchError(() => of(null))
      ).subscribe();
    }, SETTLE_MS));
  }

  delete(key: string): void {
    const prev = this.pending.get(key);
    if (prev) {
      clearTimeout(prev);
      this.pending.delete(key);
    }
    this.cache.delete(key);
    this.apiService.delete(`/user-settings/${encodeURIComponent(key)}`).pipe(
      catchError(() => of(null))
    ).subscribe();
  }

  private hydrate(): void {
    this.apiService.get('/user-settings').pipe(
      catchError(() => of([] as IUserSetting[]))
    ).subscribe((rows: IUserSetting[]) => {
      this.cache.clear();
      (rows || []).forEach(row => {
        if (row?.key) {
          this.cache.set(row.key, row.value);
        }
      });
      this.loadedSubject.next(true);
    });
  }

  private clear(): void {
    this.hydratedForSession = false;
    this.pending.forEach(timer => clearTimeout(timer));
    this.pending.clear();
    this.cache.clear();
    this.loadedSubject.next(false);
  }
}
